import {
    ReservationContractExternalTemplateField,
    ReservationContractTemplateFileFieldMapping,
    ReservationContractTemplateFileFieldMappingStatus,
    useRepositoryQuery,
} from '@mews-commander/data-access';

export type ReservationContractExternalTemplateFieldWithStatus = ReservationContractExternalTemplateField & {
    status?: ReservationContractTemplateFileFieldMappingStatus;
};

export interface TemplateDocumentFields {
    documentId: number;
    documentName: string;
    fields: ReservationContractExternalTemplateFieldWithStatus[];
    removedFields?: ReservationContractTemplateFileFieldMapping[];
}

export const useContractFieldsQuery = ({
    externalTemplateId,
    templateId,
}: {
    externalTemplateId?: string;
    templateId?: string;
}): {
    data?: TemplateDocumentFields[] | null;
    isPending: boolean;
} => {
    const { data: templateDetailData, isPending: isTemplateDetailPending } = useRepositoryQuery(
        'reservationContractTemplates',
        'getDetail',
        {
            id: templateId as string,
        },
        {
            enabled: templateId != null,
            select: ({ files }) =>
                files.map((file) => ({
                    documentId: file.externalFileId,
                    documentName: file.name,
                    removedFields: file.fieldMappings?.filter(
                        (fieldMapping) =>
                            fieldMapping.status === ReservationContractTemplateFileFieldMappingStatus.Removed
                    ),
                    fields:
                        file.fieldMappings
                            ?.map((fieldMapping) => {
                                const fieldData = fieldMapping.externalTemplateFieldData;
                                if (fieldData == null) return null;
                                return {
                                    ...fieldData,
                                    status: fieldMapping.status,
                                };
                            })
                            .filter((field) => field != null) || [],
                })),
        }
    );

    const { data: contractFieldsData, isPending: isContractFieldsPending } = useRepositoryQuery(
        'reservationContractExternalTemplates',
        'getAllFields',
        {
            externalTemplateId: externalTemplateId as string,
        },
        {
            enabled: templateId == null && externalTemplateId != null,
        }
    );

    let isPending = false;
    if (templateId != null) {
        isPending = isTemplateDetailPending;
    } else if (externalTemplateId != null) {
        isPending = isContractFieldsPending;
    }

    return {
        data: templateDetailData ?? contractFieldsData?.documentFields,
        isPending,
    };
};
